import React, { useState } from 'react';
import { ConfigProvider } from 'antd';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { api } from './utils/apiClient';
import Dashboard from './components/Dashboard';
import QualityCompliance from './modules/quality/components/QualityCompliance';
import Validation from './components/Validation';
import Issues from './components/Issues';
import LoginPage from './components/LoginPage';
import SignUpModal from './components/SignUpModal';
import Administration from './components/Administration/Administration';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import { useUser } from './contexts/UserContext';
import AI_ML from './modules/aiml/components/AI_ML';
import TrustGrid from './modules/trustgrid/components/TrustGrid';
import Library from './modules/library/Library';
import { antdTheme } from './theme';
import { ToastContainer } from './components/common';
import AppShell from './components/layout/AppShell';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './App.css';

function App() {
  const navigate = useNavigate();
  const { user, isLoggedIn, loginUser, logoutUser, roles, privileges } = useUser();
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentContent, setDocumentContent] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [actionInitiated, setActionInitiated] = useState('');
  const [actionCompleted, setActionCompleted] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSignUpSuccess = () => {
    navigate('/login'); // Navigate to Login page after sign-up
  };

  const handleLogin = async (userData) => {
    loginUser(userData);
    console.log(roles)
    console.log(privileges)
    if (userData.mustChangePassword) {
      navigate('/change-password'); // Redirect to a dedicated change password route
    } else {
      navigate('/dashboard');
    }
  };

  const handlePasswordChangeSubmit = async (formData) => {
    try {
      const response = await api.post('/change_password', {
        id: user.id,
        password: formData.password,
        mustChangePassword: false  // Reset the flag in the backend
      });
      if (response.data.message === 'Password updated successfully') {
        navigate('/dashboard');  // Redirect to dashboard after successful password change
      }
    } catch (error) {
      console.error('Error changing password:', error);
      // Handle error appropriately
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadedFileName(file.name);
      const formData = new FormData();
      formData.append('file', file);

      api.post('/file_upload', formData)
        .then(() => {
          setUploadedFileName(file.name);
          setShowModal(false);
        })
        .catch(error => {
          console.error('Error during file upload:', error);
          setUploadedFileName('Click here to upload file');
          setModalMessage('Error uploading file. Please try again.');
          setShowModal(true);
        });
    } else {
      setModalMessage('Please select a file to upload.');
      setShowModal(true);
    }
  };

  const handleAction = (action, modelType) => {
    if (!selectedFile) {
      setModalMessage('Please upload a file first.');
      setShowModal(true);
      return;
    }
    const actionStartTime = new Date().toISOString();
    setActionInitiated(action);
    setIsLoading(true);

    api.post(`/${action}`, {
      file: selectedFile,
      model_type: modelType,
      action_start: actionStartTime,
    })
    .then(response => {
      setDocumentContent(response.data.result);
      setActionCompleted(action);
      const actionEndTime = new Date().toISOString();
      const userActivityId = response.data.user_activity_id;
      if (userActivityId) {
        api.post('/audit_action_end', {
          user_activity_id: userActivityId,
          action_end: actionEndTime,
        });
      } else {
        console.error('No user activity ID returned from the action initiation call.');
      }
    })
    .catch(error => {
      console.error(`Error during ${action} request:`, error);
      setModalMessage('Failed to process the request. Please try again.');
      setShowModal(true);
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const getDisplayLabel = () => {
    if (isLoading) return '';
    switch (actionCompleted) {
      case 'summarize': return 'Summary';
      case 'identify': return 'Claims';
      default: return '';
    }
  };

  return (
    <Provider store={store}>
      <ConfigProvider theme={antdTheme}>
        <ToastContainer />
        <div className="dashboard-container">
          {isLoggedIn ? (
            <AppShell onLogout={logoutUser}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard handleFileSelect={handleFileSelect} uploadedFileName={uploadedFileName} handleAction={handleAction} actionInitiated={actionInitiated} isLoading={isLoading} user={user} documentContent={documentContent} getDisplayLabel={getDisplayLabel} showModal={showModal} modalMessage={modalMessage} closeModal={closeModal} />} />
                <Route path="/quality-compliance/*" element={<QualityCompliance />} />
                <Route path="/validation/*" element={<Validation />} />
                <Route path="/issues/*" element={<Issues />} />
                <Route path="/administration/*" element={<Administration />} />
                <Route path="/ai-ml/*" element={<AI_ML />} />
                <Route path="/library/*" element={<Library />} />
                <Route path="/trustgrid/*" element={<TrustGrid onLogout={logoutUser} />} />
                <Route path="/change-password" element={<ChangePasswordDialog user={user} onSubmit={handlePasswordChangeSubmit} onCancel={() => navigate('/dashboard')} showCheckbox={false} />} />
                <Route path="*" element={<Navigate replace to="/dashboard" />} />
              </Routes>
            </AppShell>
          ) : (
            <Routes>
              <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
              <Route path="/signup" element={<SignUpModal onSignUpSuccess={handleSignUpSuccess} />} />
              <Route path="*" element={<Navigate replace to="/login" />} />
            </Routes>
          )}
        </div>
      </ConfigProvider>
    </Provider>
  );
}

export default App;
